import { useEffect, useState } from 'react';

/**
 * Tracks the browser's online/offline state.
 *
 * Uses `navigator.onLine` as the initial value and listens to the
 * `online` / `offline` window events for updates. `navigator.onLine`
 * is best-effort (it's true whenever the OS thinks any interface is up,
 * even if the actual upstream is unreachable) but it's the best signal
 * the browser exposes without polling.
 *
 * For the offline UI we pair this with a fetch-failure event from
 * `api-client` - if the browser says we're online but a real request
 * fails with `TypeError: Failed to fetch`, that's a stronger negative
 * signal than `navigator.onLine`.
 */
export function useOnlineStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}
